import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY } from 'common/constants/services/firms';
import SummaryDefinitionsService from 'services/summaryDefinitions';
import {
  CreateDefinitionPayload,
  SUMMARY_DEFINITIONS_QUERY_KEYS,
  SummaryDefinition,
  UpdateDefinitionPayload,
  UpdateOrderPayload,
} from './types';

// ============================================
// Firm Definition Mutations
// ============================================

/**
 * Hook to create a new firm summary definition
 */
export const useCreateFirmDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, CreateDefinitionPayload>({
    mutationFn: data => service.createFirmDefinition(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

/**
 * Hook to update a firm summary definition
 */
export const useUpdateFirmDefinition = (firmId: number, definitionId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, UpdateDefinitionPayload>({
    mutationFn: data => service.updateFirmDefinition(firmId, definitionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
      queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY, firmId, definitionId] });
    },
  });
};

/**
 * Hook to delete a firm summary definition
 */
export const useDeleteFirmDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<void, Error, number>({
    mutationFn: definitionId => service.deleteFirmDefinition(firmId, definitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

/**
 * Hook to update the order of firm summary definition tabs
 */
export const useUpdateFirmDefinitionsOrder = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<{ order: number[] }, Error, UpdateOrderPayload>({
    mutationFn: data => service.updateFirmDefinitionsOrder(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

/**
 * Hook to clone a firm summary definition
 */
export const useCloneFirmDefinition = (firmId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, { sourceDefinitionId: number; newName: string }>({
    mutationFn: ({ sourceDefinitionId, newName }) =>
      service.cloneFirmDefinition(firmId, sourceDefinitionId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.firm(firmId) });
    },
  });
};

// ============================================
// Fund Definition Mutations
// ============================================

/**
 * Hook to create a new fund summary definition
 */
export const useCreateFundDefinition = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, CreateDefinitionPayload>({
    mutationFn: data => service.createFundDefinition(fundId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to update a fund summary definition
 */
export const useUpdateFundDefinition = (fundId: number, definitionId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, UpdateDefinitionPayload>({
    mutationFn: data => service.updateFundDefinition(fundId, definitionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to delete a fund summary definition
 */
export const useDeleteFundDefinition = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<void, Error, number>({
    mutationFn: definitionId => service.deleteFundDefinition(fundId, definitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to update the order of fund summary definition tabs
 */
export const useUpdateFundDefinitionsOrder = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<{ order: number[] }, Error, UpdateOrderPayload>({
    mutationFn: data => service.updateFundDefinitionsOrder(fundId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};

/**
 * Hook to clone a fund summary definition
 */
export const useCloneFundDefinition = (fundId: number) => {
  const queryClient = useQueryClient();
  const service = new SummaryDefinitionsService();

  return useMutation<SummaryDefinition, Error, { sourceDefinitionId: number; newName: string }>({
    mutationFn: ({ sourceDefinitionId, newName }) =>
      service.cloneFundDefinition(fundId, sourceDefinitionId, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.fund(fundId) });
    },
  });
};
